<template>
    <div class="flex flex-row justify-end gap-1 p-1">
        <CardTile :class="phaseTileStyle">
            <template v-if="phaseTileStatus === 'loaded'" #header>
                <div class="flex flex-row items-center gap-1">
                    <PhasesIcon class="size-4" /> {{ $t('phases-tile-title') }}
                    <TextLink :to="linkToReferenceAllocation" class="font-light whitespace-nowrap">
                        {{ $t('phases-tile-title-reference-allocation') }}
                    </TextLink>
                    <span class="font-light whitespace-nowrap">
                        {{ $t('reference-allocation-duration-days', { durationInDays }) }}
                    </span>
                </div>
            </template>
            <template #content>
                <template v-if="phaseTileStatus === 'loaded'">
                    <PhasesTile>
                        <PhasesTilePhase v-for="phase in phaseTilePhases" v-bind="phase" :key="phase.labelShort" />
                    </PhasesTile>
                </template>
                <p
                    v-else-if="phaseTileStatus === 'no-data'"
                    class="flex flex-row items-center justify-center gap-2 p-2 text-gray-500 w-full h-full text-sm"
                >
                    <WarningIcon class="size-4" /> {{ $t('no-reference-allocation') }}
                </p>
                <p
                    v-else-if="phaseTileStatus === 'loading'"
                    class="flex flex-row items-center justify-center gap-2 p-2 text-gray-500 w-full h-full text-sm"
                >
                    <PendingIcon class="size-4 animate-spin" /> {{ $t('loading-tile-data') }}
                </p>
            </template>
        </CardTile>

        <CardTile :class="locationsTileStyle">
            <template v-if="locations.length > 0" #header>
                <div class="flex flex-row items-center gap-1">
                    <ExhibitionAreaIcon class="size-4" /> {{ $t('exhibition-areas') }}
                </div>
            </template>
            <template #content>
                <p
                    v-if="locationDotsLoading"
                    class="flex flex-row items-center justify-center gap-2 p-2 text-gray-500 w-full h-full text-sm"
                >
                    <PendingIcon class="size-4 animate-spin" /> {{ $t('loading-tile-data') }}
                </p>
                <LocationDotsTile
                    v-else
                    :sum-occupied="sumOccupied"
                    :sum-optional="sumOptional"
                    :sum-total="sumTotal"
                    :count-occupied="countOccupied"
                    :count-optional="countOptional"
                    :count-total="countTotal"
                >
                    <LocationDotsRow v-for="(mappedBuildings, index) in mappedBuildingChunks" :key="index">
                        <LocationDotsBuilding
                            v-for="building in mappedBuildings"
                            :key="building.id"
                            :building-name="building.buildingName"
                            :levels="building.levels"
                            color="Project"
                            :default-tooltip-placement="building.defaultTooltipPlacement"
                            :occupied-exhibition-area-count="building.occupiedExhibitionAreaCount"
                        />
                    </LocationDotsRow>
                </LocationDotsTile>
            </template>
        </CardTile>
    </div>
</template>

<script setup lang="ts">
import CardTile from '@/components/CardTile/CardTile.vue';
import ExhibitionAreaIcon from '@/components/Icon/ExhibitionAreaIcon.vue';
import PendingIcon from '@/components/Icon/PendingIcon.vue';
import PhasesIcon from '@/components/Icon/PhasesIcon.vue';
import WarningIcon from '@/components/Icon/WarningIcon.vue';
import LocationDotsBuilding from '@/components/LocationDotsTile/LocationDotsBuilding.vue';
import LocationDotsRow from '@/components/LocationDotsTile/LocationDotsRow.vue';
import LocationDotsTile from '@/components/LocationDotsTile/LocationDotsTile.vue';
import { usePhaseTileData } from '@/components/PhasesTile/composables/usePhaseTileData';
import PhasesTile from '@/components/PhasesTile/PhasesTile.vue';
import PhasesTilePhase from '@/components/PhasesTile/PhasesTilePhase.vue';
import TextLink from '@/components/TextLink/TextLink.vue';
import { AllocationUsage } from '@/components/Timeline/allocation/types';
import { DocumentNode } from 'graphql';
import { computed } from 'vue';
import { RouteLocationRaw } from 'vue-router';
import { useProjectLocationDotsData } from '../composables/useProjectLocationDotsData';

const props = defineProps<{
    variantId: string;
    variantIndex: number;
    phaseTypes: AllocationUsage[];
    linkToReferenceAllocation: RouteLocationRaw;
    referenceAllocationQueryDocument: DocumentNode;
    referenceAllocationId?: string;
    referenceAllocationLinkLabel: string;
}>();

const phaseTileStyle = 'min-w-[313px] min-h-[127px]';
const locationsTileStyle = 'min-w-[294px] min-h-[127px]';

const variantId = computed(() => props.variantId);
const referenceAllocationId = computed(() => props.referenceAllocationId);
const {
    loading: locationDotsLoading,
    locations,
    sumOccupied,
    sumOptional,
    sumTotal,
    countOccupied,
    countOptional,
    countTotal,
    mappedBuildingChunks,
} = useProjectLocationDotsData(variantId);

const { durationInDays, phaseTilePhases, phaseTileStatus } = usePhaseTileData(
    props.referenceAllocationQueryDocument,
    props.phaseTypes,
    referenceAllocationId,
);
</script>
